import React from "react";
import { Box, Button, Tabs, Tab, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import "./OrganizationalWorkspace.css";

import ProgressBar from "../../../UI/ProgressBar";
import { Error404 } from "../../../UI/404/Error404";
import { AdminCard } from "../../../Admin/My Workspace/AdminCard";
import { useGetBooksQuery } from "../../../../redux-toolkit/features/books/bookApiSlice";
import { useGetTimeTrailsQuery } from "../../../../redux-toolkit/features/time-trails/timeTrailsApiSlice";
import { TimeTrialModal } from "../../../Admin/My Workspace/CreateTimeTrial";
import { TimeTrailCard } from "../../../Admin/My Workspace/TimeTrailCard";

const TAB_BOOKS = 0;
const TAB_TIME_TRIALS = 1;

function TabPanel(props) {
  const { children, value, index, contentClassName, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`organizational-workspace-tabpanel-${index}`}
      aria-labelledby={`organizational-workspace-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ paddingTop: 3, width: "100%" }}>
          <Typography component="div" className={contentClassName}>
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  contentClassName: PropTypes.string,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `organizational-workspace-tab-${index}`,
    "aria-controls": `organizational-workspace-tabpanel-${index}`,
  };
}

export const OrganizationalWorkspace = () => {
  const allowedAccess = useSelector((state) => state.auth.user?.allowedAccess) || [];
  const hasBooksAccess = allowedAccess.includes("SUPERADMIN_BOOKS");
  const hasTimeTrialsAccess = allowedAccess.includes("SUPERADMIN_TIMETRIAL");

  const defaultTab = hasBooksAccess
    ? TAB_BOOKS
    : hasTimeTrialsAccess
      ? TAB_TIME_TRIALS
      : TAB_BOOKS;

  const [tab, setTab] = React.useState(defaultTab);
  const [drawer, setDrawer] = React.useState(0);
  const [timeTrialOpen, setTimeTrialOpen] = React.useState(false);

  const companyId = useSelector((state) => state.auth.user.companyId);
  const selectedCompany = useSelector((state) => state.auth.selectedCompany);
  const activeCompanyId = selectedCompany || companyId;

  const {
    data: allBooksData,
    isLoading: isAllBooksLoading,
    isSuccess: isAllBooksSuccess,
    isError: isBooksError,
  } = useGetBooksQuery("org", { skip: !hasBooksAccess });

  const {
    data: timeTrialsData,
    isLoading: isTimeTrialsLoading,
    isSuccess: isTimeTrialsSuccess,
    refetch: refetchTimeTrials,
  } = useGetTimeTrailsQuery(undefined, {
    skip: !activeCompanyId || !hasTimeTrialsAccess,
  });

  React.useEffect(() => {
    if (tab === TAB_BOOKS && !hasBooksAccess && hasTimeTrialsAccess) {
      setTab(TAB_TIME_TRIALS);
    } else if (tab === TAB_TIME_TRIALS && !hasTimeTrialsAccess && hasBooksAccess) {
      setTab(TAB_BOOKS);
    }
  }, [hasBooksAccess, hasTimeTrialsAccess, tab]);

  const handleChange = (_event, newValue) => setTab(newValue);

  const showBooksLoading = isAllBooksLoading && tab === TAB_BOOKS;
  const showTimeTrialsLoading = isTimeTrialsLoading && tab === TAB_TIME_TRIALS;

  if (!hasBooksAccess && !hasTimeTrialsAccess) {
    return <Navigate to="/restricted" replace />;
  }

  if (hasBooksAccess && isBooksError) {
    return <Error404 />;
  }

  return (
    <div className="container">
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleChange}
            aria-label="organizational workspace tabs"
          >
            {hasBooksAccess && (
              <Tab
                label="Organizational Books"
                sx={{
                  textTransform: "capitalize",
                  fontWeight: "500",
                  marginRight: "20px",
                }}
                {...a11yProps(TAB_BOOKS)}
              />
            )}
            {hasTimeTrialsAccess && (
              <Tab
                label="Time Trials"
                sx={{
                  textTransform: "capitalize",
                  fontWeight: "500",
                  marginRight: "20px",
                }}
                {...a11yProps(TAB_TIME_TRIALS)}
              />
            )}
          </Tabs>

          {tab === TAB_TIME_TRIALS && hasTimeTrialsAccess && (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                sx={{ fontSize: "10px" }}
                onClick={() => setTimeTrialOpen(true)}
              >
                Add Time Trial
              </Button>
            </Box>
          )}

          {hasTimeTrialsAccess && (
            <TimeTrialModal
              timeTrialOpen={timeTrialOpen}
              setTimeTrialOpen={setTimeTrialOpen}
              isEditMode={false}
              selectedCompany={activeCompanyId}
              refetch={refetchTimeTrials}
            />
          )}
        </Box>

        {hasBooksAccess && (
          <TabPanel value={tab} index={TAB_BOOKS}>
            {showBooksLoading && <ProgressBar />}
            <div className="generic__cards">
              {isAllBooksSuccess &&
                allBooksData?.data?.books?.map((elm, index) => (
                  <div key={elm?._id || index}>
                    <AdminCard
                      setDrawer={setDrawer}
                      drawer={drawer}
                      data={elm}
                      handleBookOpen={() => {}}
                      isBook
                      isGeneric
                    />
                  </div>
                ))}
            </div>
          </TabPanel>
        )}

        {hasTimeTrialsAccess && (
          <TabPanel
            value={tab}
            index={TAB_TIME_TRIALS}
            contentClassName="organizational-workspace__timetrials-panel"
          >
            {showTimeTrialsLoading && <ProgressBar />}
            <div className="workspace__cards workspace__cards--timetrials">
              {isTimeTrialsSuccess &&
                Array.isArray(timeTrialsData?.data) &&
                timeTrialsData.data.map((elm, index) => (
                  <TimeTrailCard
                    key={elm?._id || index}
                    data={elm}
                    selectedCompany={activeCompanyId}
                    setDrawer={setDrawer}
                    drawer={drawer}
                    handleTimeTrialOpen={() => setTimeTrialOpen(true)}
                    refetch={refetchTimeTrials}
                  />
                ))}
            </div>
          </TabPanel>
        )}
      </Box>
    </div>
  );
};
